/**
 * INT-40 — "view as": an admin looks at the app as another person.
 *
 * The chosen person lives in sessionStorage (this tab only, survives a
 * reload). While set, every API call carries `X-View-As: <uid>` and that
 * person's tenant, and the backend runs the request as them with the admin
 * recorded on every audit row. The app shows them as the current user, with
 * a banner above the navbar to stop.
 */
import { useSyncExternalStore } from "react";

export interface ViewAs {
  uid: string;
  email: string | null;
  displayName: string | null;
  roles: string[];
  tenantId: string | null;
  tenantName: string | null;
  /** When viewing started, for the banner. */
  since: string;
}

const KEY = "oneself.viewAs";
const listeners = new Set<() => void>();
let cached: ViewAs | null | undefined;

function read(): ViewAs | null {
  if (cached !== undefined) return cached;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    cached = raw ? (JSON.parse(raw) as ViewAs) : null;
  } catch {
    cached = null;
  }
  return cached;
}

function write(value: ViewAs | null) {
  cached = value;
  try {
    if (value) window.sessionStorage.setItem(KEY, JSON.stringify(value));
    else window.sessionStorage.removeItem(KEY);
  } catch {
    // Session storage unavailable: the in-memory value still drives this tab.
  }
  listeners.forEach((l) => l());
}

export function getViewAs(): ViewAs | null {
  return typeof window === "undefined" ? null : read();
}

export function startViewAs(value: Omit<ViewAs, "since">) {
  write({ ...value, since: new Date().toISOString() });
}

export function stopViewAs() {
  write(null);
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function useViewAs(): ViewAs | null {
  return useSyncExternalStore(subscribe, getViewAs, () => null);
}

/** The one role the app's menus and gates read, from a roles list. */
export function primaryRole(roles: string[]): "super_admin" | "admin" | "care_manager" | "clinician" | "patient" {
  if (roles.includes("super_admin")) return "super_admin";
  if (roles.includes("admin")) return "admin";
  if (roles.includes("care_manager")) return "care_manager";
  if (roles.includes("clinician")) return "clinician";
  return "patient";
}
